import {
	AiAgentToolsQueryService,
	ApiService,
	type AiAgentTool,
	type StandardListParams,
} from '@supply-mover-2-0/core';
import type {
	ApiResponseDataModel,
	ApiResponseListModel,
} from '@supply-mover-2-0/shared';

// Re-export AiAgentTool as Tool for backward compatibility
export type Tool = AiAgentTool;

// Shape of the entries in an agent's `metadata.inputVariables` (server-defined).
export interface AgentInputVariable {
	name: string;
	type?: string;
	required?: boolean;
	label?: string;
	description?: string;
	defaultValue?: unknown;
}

export interface Agent {
	id: number;
	name: string;
	agent_type:
		| 'sales_assistant'
		| 'ar_collections'
		| 'lead_qualification'
		| 'customer_service'
		| 'reorder_reminder'
		| 'order_status'
		| 'emergency_response'
		// Code-defined agents (libs/shared/src/constants/system-agents.ts).
		// They have DB records like normal agents (so they show up in lists,
		// can be phone-assigned, etc.) but their prompt/tools live in code
		// and DB edits are ignored at runtime. `systemAgentType` identifies
		// which one (e.g. 'sales-helper').
		| 'system';
	// Note: this rest of this interface uses snake_case field names, but the
	// agents API actually returns camelCase (TypeORM default). The snake_case
	// declarations here are pre-existing and many call sites read them as
	// undefined silently. `systemAgentType` is added as camelCase because
	// it's added by `transformAgent()` and IS reliably populated.
	systemAgentType?: string;
	system_agent_type?: string;
	// Populated at runtime (camelCase from the API); MySQL tinyint may come
	// through as 0/1.
	isActive?: boolean | number;
	fa_icon?: string;
	description?: string;
	voice_profile_id?: number;
	first_message: string;
	prompt: string;
	is_active: boolean;
	conversation_count: number;
	last_used?: string;
	created_date: string;
	modified_date: string;
	creator_user_id: number;
	owner_user_id: number;
	modified_user_id?: number;
	llm_provider?: string;
	llm_model?: string;
	// Additional fields from joins
	voice_profile_name?: string;
	voice_profile_category?: string;
	agent_tools?: Array<{
		tool_id: number;
		tool?: any;
	}>;
}

export interface VoiceProfile {
	id: number;
	name: string;
	category: string;
	voice_id: string;
	is_active: boolean;
	preview_url?: string;
	characteristics?: {
		gender?: string;
		age_range?: string;
		accent?: string;
		tone?: string;
	};
}

export interface PaginatedAgentQueryResult {
	data: Agent[];
	pagination?: {
		page: number;
		limit: number;
		total: number;
		totalPages: number;
	};
	error?: string;
	success: boolean;
}

export interface AgentQueryResult {
	data: Agent | null;
	error?: string;
	success: boolean;
}

export class AgentService {
	static async getAgents(
		params?: StandardListParams,
		options?: { includeSystem?: boolean },
	): Promise<PaginatedAgentQueryResult> {
		const response = await ApiService.apiGet<
			ApiResponseListModel<Array<Agent>>
		>('/ai-agents/agents', {
			page: params?.page,
			limit: params?.pageSize,
			search: params?.search,
			sortFields:
				params?.sort != null ? JSON.stringify(params.sort) : undefined,
			filters:
				typeof params?.filters === 'object'
					? JSON.stringify(params.filters)
					: params?.filters,
			fields:
				params?.fields != null && params.fields.length > 0
					? JSON.stringify(params.fields)
					: undefined,
			cacheKey: params?.cacheKey,
			cacheTTL: params?.cacheTTL,
			// Off by default. Phone-to-Agent Assignment opts in so it can
			// route inbound numbers to phone-assignable system agents.
			includeSystem: options?.includeSystem === true ? true : undefined,
		});

		if (response.success) {
			return {
				data: Array.isArray(response.data) ? response.data : [],
				pagination: response.pagination,
				success: true,
			};
		}

		return {
			data: [],
			error: response.error || 'Failed to fetch agents',
			success: false,
		};
	}

	static async getAgent(id: number): Promise<AgentQueryResult> {
		const response = await ApiService.apiGet<ApiResponseDataModel<Agent>>(
			`/ai-agents/agents/${id}`,
		);

		if (response.success) {
			return {
				data: response.data || null,
				success: true,
			};
		}

		return {
			data: null,
			error: response.error || 'Failed to fetch agent',
			success: false,
		};
	}

	static async createAgent(agent: Partial<Agent>): Promise<AgentQueryResult> {
		const response = await ApiService.apiPost('/ai-agents/agents', agent);
		return {
			data: response.success ? response.data : null,
			...(response.error && { error: response.error }),
			success: response.success,
		};
	}

	static async updateAgent(
		id: number,
		agent: Partial<Agent>,
	): Promise<AgentQueryResult> {
		const response = await ApiService.apiPut(`/ai-agents/agents/${id}`, agent);
		return {
			data: response.success ? response.data : null,
			...(response.error && { error: response.error }),
			success: response.success,
		};
	}

	static async deleteAgent(
		id: number,
	): Promise<{ error?: string; success: boolean }> {
		const response = await ApiService.apiDelete(`/ai-agents/agents/${id}`);
		return {
			...(response.error && { error: response.error }),
			success: response.success,
		};
	}

	// Voice Profile operations
	static async getVoiceProfiles(): Promise<{
		data: VoiceProfile[];
		error?: string;
		success: boolean;
	}> {
		const response =
			await ApiService.apiGet<ApiResponseDataModel<Array<VoiceProfile>>>(
				'/voice-profiles',
			);

		if (response.success) {
			return {
				data: Array.isArray(response.data) ? response.data : [],
				success: true,
			};
		}

		return {
			data: [],
			error: response.error || 'Failed to fetch voice profiles',
			success: false,
		};
	}

	// Tool operations - delegates to core service for DRY
	static async getTools(): Promise<{
		data: Tool[];
		error?: string;
		success: boolean;
	}> {
		return AiAgentToolsQueryService.getAvailableTools();
	}

	// Agent Type labels
	static getAgentTypeLabel(type: string): string {
		const labels: Record<string, string> = {
			sales_assistant: 'Sales Assistant',
			ar_collections: 'AR Collections',
			lead_qualification: 'Lead Qualification',
			customer_service: 'Customer Service',
			reorder_reminder: 'Reorder Reminder',
			order_status: 'Order Status',
			emergency_response: 'Emergency Response',
			// System agents are code-defined and immutable from the UI; the
			// label communicates that clearly so users don't try to edit the
			// agent's prompt/tools (DB edits are ignored at runtime).
			system: 'System Agent',
		};
		return labels[type] || type;
	}


	static async getProvisionedPhones(): Promise<{
		data: Array<{ phoneNumber: string; status: string }>;
		error?: string;
		success: boolean;
	}> {
		const response = await ApiService.apiGet<
			ApiResponseDataModel<Array<{ phoneNumber: string; status: string }>>
		>('/ai-agents/agents/provisioned-phones');

		if (response.success) {
			return {
				data: Array.isArray(response.data) ? response.data : [],
				success: true,
			};
		}

		return {
			data: [],
			error: response.error || 'Failed to fetch provisioned phones',
			success: false,
		};
	}
}
